import { DialogProps, SxProps } from "@mui/material";
import ButtonOrMenuItem from "atoms/ButtonOrMenuItem";
import EdmIconButton from "atoms/Buttons/EdmIconButton";
import { CloseMenuType } from "atoms/SimpleMenu";
import AddIcon from "icons/Add.svg";
import { uppercaseFirstLetter } from "misc/formatHelpers";
import EdmDialog from "molecules/EdmDialog";
import { Fragment, ReactElement, ReactNode, RefObject, SyntheticEvent, useState } from "react";
import { ButtonProps, useTranslate } from "react-admin";

import { GenericObjectType } from "../App.types";

export type CloseDialogType = (e?: object, reason?: "backdropClick" | "escapeKeyDown", force?: boolean) => void;

export type ButtonOrMenuItemWithDialogPropsType = {
  actions?: (closeDialog: CloseDialogType) => ReactNode;
  buttonIcon?: ReactElement | null | false;
  children: (closeDialog: CloseDialogType) => ReactNode;
  className?: string;
  closeMenu?: CloseMenuType;
  dialogTitle?: string;
  fullWidth?: boolean;
  isDisabled?: boolean;
  isIconButton?: boolean;
  label: string;
  labelOpts?: {
    [key: string]: unknown;
  };
  maxWidth?: DialogProps["maxWidth"];
  onClick?: (e?: MouseEvent | SyntheticEvent) => void | Promise<void>;
  onClose?: CloseDialogType;
  passedRef?: RefObject<HTMLButtonElement> | null;
  smartCount?: number;
  dialogTitleTranslationOptions?: GenericObjectType | false;
  tooltipTitle?: string;
  tooltipOpts?: { [key: string]: string };
  "data-testid"?: string;
  size?: ButtonProps["size"];
  sx?: SxProps;
  color?: ButtonProps["color"];
  variant?: ButtonProps["variant"];
};

export default function ButtonOrMenuItemWithDialog(props: ButtonOrMenuItemWithDialogPropsType) {
  const {
    actions,
    buttonIcon,
    children,
    className,
    closeMenu,
    dialogTitle,
    fullWidth = undefined,
    isDisabled = false,
    isIconButton = false,
    label,
    labelOpts,
    maxWidth = undefined,
    onClick,
    onClose,
    passedRef,
    smartCount = 1,
    dialogTitleTranslationOptions = undefined,
    tooltipTitle,
    tooltipOpts = {},
    size = undefined,
    sx,
    color,
    variant,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const translate = useTranslate();

  const openDialog = (e?: SyntheticEvent) => {
    setIsOpen(true);
    onClick?.(e);
  };

  const closeDialog: CloseDialogType = (e) => {
    setIsOpen(false);
    onClose?.(e);
  };

  const mergedDialogTitleTranslationOptions = {
    smart_count: smartCount,
    ...(dialogTitleTranslationOptions ? dialogTitleTranslationOptions : {}),
  };

  const finalDialogTitle = dialogTitle
    ? dialogTitleTranslationOptions === false
      ? dialogTitle
      : uppercaseFirstLetter(translate(dialogTitle, mergedDialogTitleTranslationOptions))
    : uppercaseFirstLetter(translate(label, smartCount));

  const finalButtonIcon = buttonIcon === null ? null : buttonIcon === false ? undefined : buttonIcon || <AddIcon />;

  return (
    <Fragment>
      {isIconButton ? (
        <EdmIconButton
          tooltipTitle={tooltipTitle}
          smartCount={smartCount}
          passedRef={passedRef}
          onClick={openDialog}
          data-testid={props["data-testid"]}
          disabled={isDisabled}
          className={className}
          size="large"
          color={color}
          variant={variant}
        >
          {finalButtonIcon}
        </EdmIconButton>
      ) : (
        <ButtonOrMenuItem
          tooltipTitle={tooltipTitle}
          tooltipOpts={tooltipOpts}
          smartCount={smartCount}
          closeMenu={closeMenu}
          label={label}
          labelOpts={labelOpts}
          passedRef={passedRef}
          onClick={openDialog}
          data-testid={props["data-testid"]}
          disabled={isDisabled}
          className={className}
          size={size}
          sx={sx}
          color={color}
          variant={variant}
        >
          {finalButtonIcon}
        </ButtonOrMenuItem>
      )}

      <EdmDialog
        title={finalDialogTitle}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
        open={isOpen}
        onClose={closeDialog}
        actions={actions}
        PaperProps={{ onClick: (e: SyntheticEvent) => e.stopPropagation() }}
      >
        {children}
      </EdmDialog>
    </Fragment>
  );
}
